import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TenantFormData } from "@/types/tenant";
import { TenantWizardSteps } from "./TenantWizardSteps";
import { BusinessInfoStep } from "./steps/BusinessInfoStep";
import { ContactInfoStep } from "./steps/ContactInfoStep";
import { BillingInfoStep } from "./steps/BillingInfoStep";
import { AdminInviteStep } from "./steps/AdminInviteStep";
import { ReviewStep } from "./steps/ReviewStep";

interface TenantOnboardingWizardProps {
  onComplete: (data: TenantFormData) => Promise<void>;
  isSubmitting?: boolean;
}

const STEPS = [
  { id: 1, title: "Business Info", description: "Company details" },
  { id: 2, title: "Contact", description: "Contact information" },
  { id: 3, title: "Billing", description: "Billing contact" },
  { id: 4, title: "Admin", description: "Invite admin (optional)" },
  { id: 5, title: "Review", description: "Confirm details" },
];

const initialFormData: TenantFormData = {
  name: "",
  email: "",
  industry: "",
  company_size: "",
  expected_device_count: undefined,
  tenant_type: "customer",
  can_parent_manage: true,
  phone: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  billing_contact_name: "",
  billing_contact_email: "",
  billing_same_as_primary: true,
  invite_admin: false,
  admin_email: "",
  admin_name: "",
};

export function TenantOnboardingWizard({ onComplete, isSubmitting }: TenantOnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<TenantFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateFormData = (updates: Partial<TenantFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear errors for updated fields
    const clearedErrors = { ...errors };
    Object.keys(updates).forEach(key => delete clearedErrors[key]);
    setErrors(clearedErrors);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.name.trim()) newErrors.name = "Company name is required";
        break;
      case 2:
        if (!formData.email.trim()) {
          newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = "Invalid email format";
        }
        break;
      case 3:
        if (!formData.billing_same_as_primary) {
          if (!formData.billing_contact_email?.trim()) {
            newErrors.billing_contact_email = "Billing email is required";
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billing_contact_email)) {
            newErrors.billing_contact_email = "Invalid email format";
          }
        }
        break;
      case 4:
        if (formData.invite_admin && !formData.admin_email?.trim()) {
          newErrors.admin_email = "Admin email is required when inviting";
        } else if (formData.invite_admin && formData.admin_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
          newErrors.admin_email = "Invalid email format";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (validateStep(currentStep)) {
      await onComplete(formData);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <BusinessInfoStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 2:
        return <ContactInfoStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 3:
        return <BillingInfoStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 4:
        return <AdminInviteStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 5:
        return <ReviewStep formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <TenantWizardSteps steps={STEPS} currentStep={currentStep} />

      {/* Step Content */}
      <Card disableHover>
        <CardContent className="pt-6">
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 1 || isSubmitting}
        >
          Back
        </Button>
        
        {currentStep < STEPS.length ? (
          <Button onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Creating Tenant..." : "Create Tenant"}
          </Button>
        )}
      </div>
    </div>
  );
}
